'use client';

import type { SonarrInstance, RadarrInstance, WebhookConfig, PayloadField } from './types';

const DEFAULT_PAYLOAD_FIELDS_SONARR: PayloadField[] = [
  { id: 'pf-s-1', key: 'event', value: 'media_dispatched', enabled: true },
  { id: 'pf-s-2', key: 'source', value: '{{source}}', enabled: true },
  { id: 'pf-s-3', key: 'title', value: '{{item.title}}', enabled: true },
  { id: 'pf-s-4', key: 'qualityProfile', value: '{{item.qualityProfile}}', enabled: true },
];

const DEFAULT_PAYLOAD_FIELDS_RADARR: PayloadField[] = [
  { id: 'pf-r-1', key: 'event', value: 'media_dispatched', enabled: true },
  { id: 'pf-r-2', key: 'source', value: '{{source}}', enabled: true },
  { id: 'pf-r-3', key: 'title', value: '{{item.title}}', enabled: true },
  { id: 'pf-r-4', key: 'rootFolder', value: '/media/movies', enabled: true },
];

const KEYS = {
  sonarr: 'mediadispatch_sonarr_instances',
  radarr: 'mediadispatch_radarr_instances',
  webhooks: 'mediadispatch_webhooks',
};

function load<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function save<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(key, JSON.stringify(value));
}

const defaultSonarr: SonarrInstance[] = [
  {
    id: 'sonarr-default',
    name: 'Sonarr (Default)',
    url: 'http://localhost:8989',
    apiKey: '',
    qualityProfile: 'HD-1080p',
    payloadFields: DEFAULT_PAYLOAD_FIELDS_SONARR,
    createdAt: new Date().toISOString(),
  },
];

const defaultRadarr: RadarrInstance[] = [
  {
    id: 'radarr-default',
    name: 'Radarr (Default)',
    url: 'http://localhost:7878',
    apiKey: '',
    qualityProfile: 'Ultra-HD',
    rootFolder: '/media/movies',
    payloadFields: DEFAULT_PAYLOAD_FIELDS_RADARR,
    createdAt: new Date().toISOString(),
  },
];

const defaultWebhooks: WebhookConfig[] = [
  {
    id: 'webhook-default',
    name: 'My Webhook',
    url: 'https://example.com/webhook',
    method: 'POST',
    createdAt: new Date().toISOString(),
  },
];

export const configStore = {
  getSonarrInstances: (): SonarrInstance[] => load(KEYS.sonarr, defaultSonarr),
  saveSonarrInstances: (instances: SonarrInstance[]) => save(KEYS.sonarr, instances),

  getRadarrInstances: (): RadarrInstance[] => load(KEYS.radarr, defaultRadarr),
  saveRadarrInstances: (instances: RadarrInstance[]) => save(KEYS.radarr, instances),

  getWebhooks: (): WebhookConfig[] => load(KEYS.webhooks, defaultWebhooks),
  saveWebhooks: (webhooks: WebhookConfig[]) => save(KEYS.webhooks, webhooks),

  makeDefaultPayloadFieldsSonarr: (): PayloadField[] =>
    DEFAULT_PAYLOAD_FIELDS_SONARR.map((f) => ({ ...f, id: `pf-s-${Date.now()}-${Math.random()}` })),

  makeDefaultPayloadFieldsRadarr: (): PayloadField[] =>
    DEFAULT_PAYLOAD_FIELDS_RADARR.map((f) => ({ ...f, id: `pf-r-${Date.now()}-${Math.random()}` })),
};
